import { useState, useEffect, useRef } from "react"
import { useShallow } from "zustand/react/shallow"
import scrollToElement from "utilities/scrollToElement"

// components
import RJCommentList from "../../../components/RJCommentList/RJCommentList"
import { Tooltip } from "antd"
import DeleteForkSvg from "containers/studies/Study/subcomponents/Authoring/assets/DeleteForkSvg"
import ForkDeleteModal from "containers/studies/Study/subcomponents/Authoring/RJS/components/ForkDeleteModal"
import useRegistryBadges from "containers/studies/Study/subcomponents/Authoring/RJS/components/Badges/useRegistryBadges/useRegistryBadges.jsx"

// context
import useAuthoringCommentsStore from "../../../../hooks/stores/useAuthoringCommentsStore"
import useAuthoringViewStore from "../../../../hooks/stores/useAuthoringViewStore"
import useAuthoringDataStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringDataStore"
import useResultsTableViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableViewStore"

// constants
import { SCROLLABLE_AUTHORING_CONTAINER } from "constants/authoring"

// styles
import "./RJTableCommentContainer.scss"

const RJTableCommentContainer = ({
  jpath,
  label,
  openCommentsForm,
  setOpenCommentsForm,
  setShowMenu,
  showMenu,
  style = {},
  modalStyle = {},
  enableForking = false,
  deleteForkCallback = () => {},
  registry,
}) => {
  const { tableView } = useResultsTableViewStore()
  const { getBadges } = useRegistryBadges()
  const { formView, readOnly, isReviewerView } = useAuthoringViewStore(
    useShallow((state) => ({
      formView: state.formView,
      readOnly: state.readOnly,
      isReviewerView: state.isReviewerView,
    }))
  )

  const { openComment, commentsMap } = useAuthoringCommentsStore(
    useShallow((state) => ({
      openComment: state.openComment,
      commentsMap: state.commentsMap,
    }))
  )

  const { forkedStudyData, setForkedStudyData } = useAuthoringDataStore(
    useShallow((state) => ({
      forkedStudyData: state.forkedStudyData,
      setForkedStudyData: state.setForkedStudyData,
    }))
  )

  const [openDeleteForkedModal, setOpenDeleteForkedModal] = useState(false)
  const [comments, setComments] = useState([])
  let openCommentRef = useRef(null)
  const commentListRef = useRef()
  const labelMenuRef = useRef()

  useEffect(() => {
    if (commentsMap) {
      if (commentsMap[jpath]) {
        let comments = commentsMap[jpath]
        setComments(comments)
      } else {
        setComments([])
      }
    }
  }, [commentsMap])

  useEffect(() => {
    if (openComment && openComment === jpath) {
      if (setShowMenu) {
        setShowMenu(true)
      }
      setOpenCommentsForm(true)
      scrollToField(jpath)
      openCommentRef.current = openComment
    }
  }, [openComment])

  useEffect(() => {
    if (forkedStudyData?.[tableView]?.[jpath]) {
      setShowMenu(true)
    }
  }, [forkedStudyData?.[tableView]?.[jpath], showMenu])

  const scrollToField = (jpath) => {
    const scrollableElement = isReviewerView
      ? document.getElementById(SCROLLABLE_AUTHORING_CONTAINER.REVIEW_ID)
      : document.getElementsByClassName(SCROLLABLE_AUTHORING_CONTAINER.FORM_CLASSNAME)[0]

    const targetElement = document.getElementById(jpath)

    if (targetElement && scrollableElement) {
      scrollToElement({
        scrollableElement,
        targetElement,
        scrollType: "instant",
      })
    }
  }

  const onDeleteForkField = () => {
    let tempForkedFields = { ...forkedStudyData }
    delete tempForkedFields[tableView][jpath]
    setForkedStudyData(tempForkedFields)
    setOpenDeleteForkedModal(false)
    setShowMenu(false)
    deleteForkCallback({ registry, jpath })
  }

  const onForkField = () => {
    let tempForkedFields = { ...forkedStudyData }
    if (tempForkedFields?.[tableView]) {
      tempForkedFields[tableView][jpath] = true
    } else {
      tempForkedFields = {
        ...tempForkedFields,
        [tableView]: {
          [jpath]: true,
        },
      }
    }
    setForkedStudyData(tempForkedFields)
  }

  const renderFork = () => {
    const registryTitle = formView?.text

    if (forkedStudyData?.[tableView]?.[jpath]) {
      return (
        <div className="results-fork-container">
          {getBadges([formView.id])}
          {readOnly !== true && (
            <Tooltip title={`Remove ${registryTitle} forked field`}>
              <span className="delete-fork-wrapper">
                <DeleteForkSvg onClick={() => setOpenDeleteForkedModal(true)} />
              </span>
            </Tooltip>
          )}
        </div>
      )
    }

    return (
      <div className="results-fork-container">
        <Tooltip title={`Fork for ${registryTitle}`}>
          <div
            className="results-fork-icon fal fa-code-merge"
            onClick={onForkField}
          />
        </Tooltip>
      </div>
    )
  }

  return (
    <div
      className={`rj-table-comment-container ${showMenu ? "show-container" : "hide-container"}`}
      ref={labelMenuRef}
      id={jpath}
    >
      {enableForking && formView.id !== "global" && renderFork()}
      <i
        className={`fas comment-icon ${
          comments && comments.length > 0 ? "has-comment fa-comment-alt-dots" : "fa-comment-alt-medical"
        }`}
        onClick={() => {
          setOpenCommentsForm(true)
        }}
        style={style}
      />
      {openCommentsForm && (
        <div
          className="animate__animated animate__fadeIn animate__fadeInUp animate__faster comment-modal-container"
          style={modalStyle}
        >
          <RJCommentList
            label={label}
            jpath={jpath}
            openComment={openComment}
            openForm={openCommentsForm}
            setOpenForm={setOpenCommentsForm}
            setShowMenu={setShowMenu}
            comments={comments}
          />
        </div>
      )}
      <ForkDeleteModal
        onHide={() => setOpenDeleteForkedModal(false)}
        showModal={openDeleteForkedModal}
        onSubmit={onDeleteForkField}
      />
    </div>
  )
}

export default RJTableCommentContainer
